import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  InputLabel,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { BaseTextInput, Text, TitleBar } from '@redacted/react-components';
import SignupSuccess from './204';
import ServerError from './500';
import getAPIHost from '../utils/apiHelper';

function Signup() {
  const apiHost = getAPIHost();
  const codewordCount = 4;
  const [isLoading, setisLoading] = useState(false);
  const [isSubmitted, setisSubmitted] = useState(false);
  const [statusCode, setStatusCode] = useState(false);
  // const [invitationCode, setInvitationCode] = useState('');
  const useStyles = makeStyles({
    invitationCode: {
      position: 'relative',
      '&:after': {
        content: '"-"',
        position: 'absolute',
        top: '50%',
        right: 0,
        bottom: 'auto',
        left: 'auto',
        transform: 'translate(50%, -50%)',
      },
    },
    hiddenInput: {
      '& .MuiInputBase-root': { display: 'none' },
    },
  });
  const classes = useStyles();
  const codewordInitialValues = {};
  const codewordValidationSchemas = {};
  [...Array(codewordCount)].forEach((_, i) => {
    codewordInitialValues[`codeword${i + 1}`] = '';
    codewordValidationSchemas[`codeword${i + 1}`] = yup.string()
      .matches(new RegExp(/^[a-zA-Z0-9]+$/), 'Invitaiton code words may only contain letters or numbers.')
      .max(9, 'Invitation code words must be at most nine characters.')
      .required('Four word invitation code is required.');
  });
  const formik = useFormik({
    initialValues: {
      email: '',
      nickname: '',
      // status_code: 500,
      code: '',
      ...codewordInitialValues,
    },
    onSubmit: (values) => {
      setisLoading(true);
      setisSubmitted(true);
      fetch(
        `https://${apiHost}/invitation`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        },
      ).then((response) => {
        setStatusCode(response.status);
        if (response.status === 403) {
          [...Array(codewordCount)].forEach((_, i) => {
            formik.errors[`codeword${i + 1}`] = 'Invitation code is invalid.';
          });
        }
      }).catch(() => {
        [...Array(codewordCount)].forEach((_, i) => {
          formik.errors[`codeword${i + 1}`] = 'Invitation code is invalid.';
        });
      }).finally(() => {
        setisLoading(false);
      });
    },
    validationSchema: yup.object({
      email: yup.string().email('Email address is invalid.').required('Email address is required.'),
      nickname: yup.string().matches(/\w/, 'Nickname may only contain letters, numners, spaces, dashes, and underscores.'),
      ...codewordValidationSchemas,
    }),
  });
  const hasCodewordError = () => {
    let hasError = false;
    [...Array(codewordCount)].forEach((_, i) => {
      if ((formik.touched[`codeword${i}`] && !!formik.errors[`codeword${i}`]?.length)) {
        hasError = true;
      }
    });
    return hasError;
  };
  const getCodewordHelperText = () => {
    let errorText;
    [...Array(codewordCount)].forEach((_, i) => {
      if ((formik.touched[`codeword${i}`] && !!formik.errors[`codeword${i}`]?.length)) {
        if (!errorText) errorText = formik.errors[`codeword${i}`];
      }
    });
    return errorText;
  };

  useEffect(() => {
    formik.values.code = [...Array(codewordCount)].map((_, i) => formik.values[`codeword${i + 1}`]).join('-');
  }, [[...Array(codewordCount)].map((_, i) => formik.values[`codeword${i + 1}`])]);

  const form = (
    <Box width="75%" margin="0 auto">
      <TitleBar title="Sign up to TIDE" />
      <Typography component="h2" variant="subtitle1">
        Redacted TIDE is an analytic engine designed to help you understand threats.
      </Typography>
      <Box
        component="form"
        id="signup_form"
        marginTop={18}
        onSubmit={formik.handleSubmit}
      >
        <Grid container direction="column" spacing={6}>
          <Grid item>
            <BaseTextInput
              error={formik.touched.email && !!formik.errors.email?.length}
              helperText={formik.touched.email && formik.errors.email}
              label="Email"
              name="email"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              placeholder="Email"
              setValue={() => {}}
              type="email"
              value={formik.values.email}
            />
          </Grid>
          <Grid item>
            <BaseTextInput
              error={formik.touched.nickname && !!formik.errors.nickname?.length}
              helperText={formik.touched.nickname && formik.errors.nickname}
              label="Nickname (optional)"
              name="nickname"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              placeholder="e.g. John Smith or JSmith or John"
              setValue={() => {}}
              value={formik.values.nickname}
            />
          </Grid>
          <Grid item>
            <InputLabel>
              <Text variant="globalUpperSmall" color="fogGray">Invitation Code</Text>
            </InputLabel>
            <Grid container spacing={6}>
              <Grid item xs className={classes.invitationCode}>
                <BaseTextInput
                  error={formik.touched.codeword1 && !!formik.errors.codeword1?.length}
                  name="codeword1"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  setValue={() => {}}
                  value={formik.values.codeword1}
                />
              </Grid>
              <Grid item xs className={classes.invitationCode}>
                <BaseTextInput
                  error={formik.touched.codeword2 && !!formik.errors.codeword2?.length}
                  name="codeword2"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  setValue={() => {}}
                  value={formik.values.codeword2}
                />
              </Grid>
              <Grid item xs className={classes.invitationCode}>
                <BaseTextInput
                  error={formik.touched.codeword3 && !!formik.errors.codeword3?.length}
                  name="codeword3"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  setValue={() => {}}
                  value={formik.values.codeword3}
                />
              </Grid>
              <Grid item xs>
                <BaseTextInput
                  error={formik.touched.codeword4 && !!formik.errors.codeword4?.length}
                  name="codeword4"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  setValue={() => {}}
                  value={formik.values.codeword4}
                />
              </Grid>
            </Grid>
            <BaseTextInput
              className={classes.hiddenInput}
              error={hasCodewordError()}
              helperText={getCodewordHelperText()}
              inputProps={{ readOnly: true }}
              name="code"
              value={formik.values.code}
            />
          </Grid>
          <Grid item>
            <Button
              color="primary"
              disabled={
                isLoading
                || (formik.touched.email && !!formik.errors.email?.length)
                || (formik.touched.nickname && !!formik.errors.nickname?.length)
                || (!isSubmitted && hasCodewordError())
              }
              fullWidth
              type="submit"
              variant="contained"
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
  return (
    <>
      {(!statusCode || statusCode === 403) && form}
      {statusCode === 204 && <SignupSuccess />}
      {statusCode === 500 && (
        <ServerError
          errorAction={() => {
            setisSubmitted(false);
            setStatusCode(false);
          }}
        />
      )}
    </>
  );
}

export default Signup;
